import { Group, Modal, Stack, Text, useMantineTheme } from "@mantine/core"
import { useDisclosure } from "@mantine/hooks"
import { notifications } from "@mantine/notifications"
import { IconAlertTriangle } from "@tabler/icons-react"
import {
  SortingState,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table"
import { Button } from "@tfg/ui"
import { useState } from "react"

import { AdminResponses } from "@tfg/types/endpoints/admin"

import { Table } from "~/components/table/Table"

import { useColumns } from "./columns"
import { useDeleteCustomFieldMutation } from "./hooks"

type Props = {
  customFieldDefinitions: AdminResponses.CustomFieldDefinition[]
  handleUpdate: (customField: AdminResponses.CustomFieldDefinition) => void
}

export function CustomFieldsAdmin({
  customFieldDefinitions,
  handleUpdate,
}: Props) {
  const { mutate: deleteCustomField } = useDeleteCustomFieldMutation({
    onSuccess: () => {
      close()
    },
    onError: (error) => {
      notifications.show({
        title: "Error",
        message: error.response?.data
          ? JSON.stringify(error.response?.data)
          : error.message,
        color: "red",
      })
    },
  })
  const [opened, { open, close }] = useDisclosure()
  const [selectedCustomFieldId, setSelectedCustomFieldId] = useState<number>(0)

  const handleDelete = (id: number) => {
    setSelectedCustomFieldId(id)
    open()
  }

  const columns = useColumns(handleDelete, handleUpdate)
  const [sorting, setSorting] = useState<SortingState>([
    { id: "name", desc: false },
  ])

  const table = useReactTable({
    data: customFieldDefinitions,
    columns,
    state: {
      sorting,
    },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  })

  const { colors } = useMantineTheme()

  return (
    <>
      <Table table={table} pagination={false} />
      <Modal
        opened={opened}
        onClose={close}
        title={
          <Group justify="center">
            <IconAlertTriangle color={colors.yellow[5]} />
            <Text fz="lg">Warning</Text>
          </Group>
        }
      >
        <Stack style={{ textAlign: "center" }}>
          <Text fz="lg">
            This custom field <strong>might have some</strong> products of this
            organization associated with it. Are you sure you want to delete
            this custom field?
          </Text>
          <Group justify="center">
            <Button onClick={close} variant="secondary">
              Cancel
            </Button>
            <Button
              color="red"
              onClick={() => deleteCustomField(selectedCustomFieldId)}
            >
              Delete
            </Button>
          </Group>
        </Stack>
      </Modal>
    </>
  )
}
